import { UnknownPageError } from './unknown_page_error'

// Presenter pages
import presenterDashboard from './presenter/dashboard'
import activities from './presenter/activities'
import reports from './presenter/reports'

// Authentication pages
import termsAgreement from './auth/terms_agreement'
import logout from './auth/logout'
import login from './auth/login'
import resetPassword from './auth/reset_password'
import signup from './auth/signup'
import outOfBandComplete from './auth/out_of_band_complete'

// Participant pages
import participantHome from './participant/home'
import participantLegacyVote from './participant/legacy_vote'
import participantHistory from './participant/history'
import participantLiveResponse from './participant/live_response'
import participantStaticResponse from './participant/static_response'
import participantRegistrations from './participant/registrations'
import participantRegister from './participant/register'
import participantRegisterSearch from './participant/register_search'
import participantBecomePresenter from './participant/become_presenter'

// Agave pages
import agavePresenter from './agave/presenter'

// Profile pages
import profile from './settings/profile'

// Slideware pages
import slideware from './integration/slideware'

// Support pages
import support from './support'
import feedback from './support/feedback'

// System pages
import welcome from './welcome'
import notFound from './not_found'
import pleaseWait from './auth/please_wait'

// Webex pages
import webexPresenter from './webex/presenter'

import store from '@polleverywhere/redux'
import { slice as appSlice } from '../slices/app'
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import { experimentHelper } from '../util/experiment_helper'
import { navigate } from '../util/legacy_navigate'

export const systemPages = {
  terms_agreement: {
    handler: termsAgreement,
    userRequired: true
  },
  settings: {
    handler: profile,
    userRequired: true
  },
  login: {
    handler: login,
    userRequired: false
  },
  'log in': {
    handler: login,
    userRequired: false
  },
  logout: {
    handler: logout,
    userRequired: false
  },
  reset_password: {
    handler: resetPassword,
    userRequired: false
  },
  signup: {
    handler: signup,
    userRequired: false
  },
  support: {
    handler: support,
    userRequired: false
  },
  feedback: {
    handler: feedback,
    userRequired: false
  },
  welcome: {
    handler: welcome,
    userRequired: true
  },
  not_found: {
    handler: notFound,
    userRequired: false
  }
}

export const pagesByRole = {
  participant: {
    home: {
      handler: participantHome,
      userRequired: false,
      default: true
    },
    legacy_vote: {
      handler: participantLegacyVote,
      userRequired: false
    },
    response_history: {
      handler: participantHistory,
      userRequired: false
    },
    live_response: {
      handler: participantLiveResponse,
      userRequired: false
    },
    static_response: {
      handler: participantStaticResponse,
      userRequired: false
    },
    registrations: {
      userRequired: true,
      handler: participantRegistrations
    },
    register: {
      handler: participantRegister,
      userRequired: false
    },
    register_search: {
      handler: participantRegisterSearch,
      userRequired: false
    },
    present: {
      handler: participantBecomePresenter,
      userRequired: false
    }
  },
  slideware: {
    home: {
      handler: presenterDashboard,
      userRequired: true
    },
    activities: {
      handler: activities,
      userRequired: true,
      default: true
    },
    reports: {
      handler: reports,
      userRequired: true
    },
    slideware: {
      handler: slideware,
      userRequired: (state) => {
        // these 2 are special pages
        if (['modal'].includes(state.appStates.slideware.get('page'))) {
          return false
        }

        return true
      }
    }
  },
  presenter: {
    home: {
      handler: presenterDashboard,
      userRequired: true
    },
    activities: {
      handler: activities,
      userRequired: true,
      default: true
    },
    reports: {
      handler: reports,
      userRequired: true
    },
    slideware: {
      handler: slideware,
      userRequired: (state) => {
        // these 2 are special pages
        if (['modal'].includes(state.appStates.slideware.get('page'))) {
          return false
        }

        return true
      }
    }
  },
  agave: {
    activities: {
      handler: activities,
      userRequired: true,
      default: true
    },
    presenter: {
      handler: agavePresenter,
      userRequired: true
    }
  },
  embed: {
    blank: {
      handler: false,
      userRequired: false,
      default: true
    },
    live_response: {
      handler: participantLiveResponse,
      userRequired: false
    },
    static_response: {
      handler: participantStaticResponse,
      userRequired: false
    },
    register: {
      handler: participantRegister,
      userRequired: false
    }
  },
  auth: {
    out_of_band_complete: {
      handler: outOfBandComplete,
      userRequired: true
    },
    please_wait: {
      handler: pleaseWait,
      userRequired: false,
      default: true
    }
  },
  webex: {
    presenter: {
      handler: webexPresenter,
      userRequired: false,
      default: true
    }
  }
}

export function getPageConfig (role, page) {
  const pages = Object.assign({}, systemPages, pagesByRole[role])
  if (!pages[page]) throw new UnknownPageError(`Unknown page: ${page}`)
  return pages[page]
}

export async function getContent (state, page, lastPage, pageOptions = {}) {
  // must content to agreement before continuing
  if (page !== 'terms_agreement' && store.getState()[profileSlice.name].participant?.must_consent) {
    store.dispatch(appSlice.pageUpdated('terms_agreement'))
    return
  }

  const { role, defaultPage } = store.getState()[appSlice.name]
  const { userRequired, handler } = getPageConfig(role, page)

  if (!handler) {
    return
  }

  let isUserRequired = userRequired
  if (typeof userRequired === 'function') {
    isUserRequired = userRequired.call(this, state)
  }

  let content

  const loggedIn = appSlice.selectIsFullyLoggedIn(store.getState())

  // if the page requires a user return the login handler instead
  if (isUserRequired && !loggedIn) {
    // only start rendering if the session's been loaded.
    // state.loggedIn === false could be profile not loaded or it's loaded and the user is logged in.
    // It probably makes sense to separate those 2 meanings.
    // state.get('ready') means a profile's been loaded
    if (store.getState()[appSlice.name].ready) {
      content = await login.call(this, state, this.options, page, pageOptions)
    }
  } else {
    content = await handler.call(this, state, this.options, lastPage, pageOptions)

    // the state has moved on already
    const currentPage = store.getState()[appSlice.name].page

    if (page !== currentPage) {
      console.log(`Page changed to ${currentPage} while loading ${page}, aborting rendering`)
      return
    }
  }

  // if page is undefined, go back to lastPage
  content?.on?.('navigate', (page, options = {}) => {
    // special pages to switch between participating and presenting
    if (page === 'participate') {
      store.dispatch(appSlice.roleUpdated('participant'))
      return
    }

    if (!page) {
      // You can only go back to these pages. If it's not in the list,
      // default to activities
      if (['activities', 'reports', 'remote'].includes(lastPage)) {
        page = lastPage
      } else {
        page = defaultPage
      }
    }

    // TODO: update callers to set these directly on the appropriate slices before navigating
    // This is a temporary hack to store pageOptions during this transition from Backbone to Redux
    navigate(page, options)
  })

  if (content?.experiments) {
    // if the content has experiments load these before
    // rendering anything
    await Promise.all(
      content.experiments.map(e => experimentHelper.segmentFor(e.name))
    )
  }

  return content
}
